import { ConversationDetails } from "@/types/conversation/conversation";
import { buildTimeDistribution } from "../utils/aggregates";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface InsightsTimeDistributionProps {
  conversations: ConversationDetails[];
}

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const formatHour = (hour: number) => {
  const period = hour < 12 ? "AM" : "PM";
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}${period}`;
};

export const InsightsTimeDistribution = ({ conversations }: InsightsTimeDistributionProps) => {
  const { byHour, byWeekday, peakHour, peakWeekday } = buildTimeDistribution(conversations);
  const maxHourCount = Math.max(...byHour, 1);
  const maxWeekdayCount = Math.max(...byWeekday, 1);
  const hasData = byHour.some((n) => n > 0);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden h-full">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
        <GenieIcon
          name="microphone"
          size={24}
          color="#4E9CFF"
          secondaryColor="#FF4DB8"
          gradientEnabled
          label="When calls happen"
        />
        <div>
          <h3 className="text-base font-semibold text-gray-900">When calls happen</h3>
          <p className="text-xs text-gray-500">
            {hasData && peakHour !== null && peakWeekday !== null
              ? `Busiest around ${formatHour(peakHour)} on ${WEEKDAY_LABELS[peakWeekday]}`
              : "Time-of-day and day-of-week patterns"}
          </p>
        </div>
      </div>

      <div className="p-5 space-y-6">
        {!hasData ? (
          <div className="text-center py-8">
            <p className="text-sm text-gray-500">No conversations with a date in this range yet.</p>
          </div>
        ) : (
          <>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Day of week</p>
              <div className="flex items-end gap-2 h-20">
                {byWeekday.map((count, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <div
                      className="w-full rounded-t-md bg-gradient-to-t from-[#4E9CFF] to-[#FF4DB8]"
                      style={{ height: `${Math.max(4, (count / maxWeekdayCount) * 100)}%` }}
                      title={`${count} call${count === 1 ? "" : "s"}`}
                    />
                    <span className="text-[10px] text-gray-500">{WEEKDAY_LABELS[i]}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Time of day</p>
              <div className="flex items-end gap-[3px] h-16">
                {byHour.map((count, hour) => (
                  <div
                    key={hour}
                    className="flex-1 rounded-t-sm bg-gradient-to-t from-[#4E9CFF] to-[#FF4DB8] opacity-80"
                    style={{ height: `${Math.max(4, (count / maxHourCount) * 100)}%` }}
                    title={`${formatHour(hour)}: ${count} call${count === 1 ? "" : "s"}`}
                  />
                ))}
              </div>
              <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                <span>12AM</span>
                <span>6AM</span>
                <span>12PM</span>
                <span>6PM</span>
                <span>11PM</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
